import React from 'react';
import type User from '../models/User';
import type Org from '../models/Org';
import { matchPath } from 'react-router';
import { environment } from '../environments/environment';
import get from 'lodash/fp/get';
import getOr from 'lodash/fp/getOr';
import { getCurrentUser } from 'aws-amplify/auth';
import { type StoryMedia } from '../models/Story';
import type Story from '../models/Story';
import type Keyword from '../models/Keyword';
import loadImage from 'blueimp-load-image';
import {
  EntityTypes,
  Roles,
  VisionLikelihood,
  URL_PROTOCOL,
  Services,
  EntityAliases,
  SubmissionTypes,
  AdvancementTypes,
} from '../constants';
import type { Entities, Entity, SelectedConnections, SocialNetworks } from '../types';
import { type FormikHelpers as FormikActions, type FormikValues } from 'formik';
import message from 'antd/lib/message';
import { type QueryClient } from '@tanstack/react-query';
import type Competition from '../models/Competition';
import type { OrgConnection } from '../models/OrgConnection';

export function createUrl(url: string, params: Object) {
  try {
    const fullUrl = new URL(url);

    Object.entries(params).forEach(([k, v]) => {
      fullUrl.searchParams.append(k, v);
    });
    return fullUrl.toString();
  } catch (error) {
    console.error('Invalid URL:', error);
    return '';
  }
}

export const isUserAuthorized = async () => {
  try {
    await getCurrentUser();
    return true;
  } catch (error) {
    return false;
  }
};

export function getOrgById(user: User, id: string) {
  return user.orgs.find((org: Org) => org._id === id)!;
}

export function getOrgByUrl(user: User, url: string) {
  return user.orgs.find((org: Org) => org.url === url)!;
}

export function getUrlParam(path: string, routePath: string, paramName: string, exact = false) {
  const match = matchPath(
    {
      path: routePath,
      end: exact,
    },
    path,
  );
  return get(`params.${paramName}`, match);
}

export function formatUSPhoneNumber(phoneNumber: string) {
  const x = phoneNumber.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  if (x) {
    return !x[2] ? x[1] : `(${x[1]}) ${x[2]}${x[3] ? `-${x[3]}` : ''}`;
  }
  return null;
}

export function getCurrentOrgId(user: User, pathname = window.location.pathname) {
  const route = matchPath({ path: '/:orgPath' }, pathname);
  if (!route) {
    return '';
  }
  const { _id } = getOrgByUrl(user, get('params.orgPath', route));
  return _id;
}

export function roleAllowed(roles: string[], member: any) {
  if (!roles || !member) return false;
  return roles.includes(member.role);
}

export function getFanboardEmbedCode(boardId: string, folderId: string, displayType: string) {
  return `<div id="bragify-feed"></div>
  <script src="${environment.fanboardUrl}"></script>
  <script type="text/javascript">
    bragify.fanBoard({ accountId: '${boardId}', folderId: '${folderId}', domElementId: 'bragify-feed', displayType: '${displayType}' });
  </script>`;
}

export function getBoardXML(boardId: string, folderId: string, displayType: string) {
  return `https://board-videos.gigg.com/${environment.env}/inboxes/${boardId}/boards/${folderId}/${displayType}.xml`;
}

export function isPinned(story: Story) {
  return story.folders && Object.keys(story.folders).length;
}

export function extractKeywordFeedErrorsFromConnections(
  keywords: Keyword[],
  selectedConnections: SelectedConnections = [] as SelectedConnections,
  networkSignInTimes: Array<{ type: string; connectedDate: string }> = [],
) {
  return keywords
    .map((keyword: Keyword) => {
      if (!keyword.feeds) {
        return null;
      }
      const errors = Object.entries(keyword.feeds)
        .map(([key]) => {
          const error = getOr('', 'pullInfo.error', keyword.feeds[key]);
          const pullTime = getOr('', 'pullInfo.pullTime', keyword.feeds[key]);
          const filteredError = error?.includes('Not signed into') || error === 'null' ? null : error;
          return { network: key, error: filteredError, pullTime };
        })
        .filter((item) => {
          const currentNetwork =
            item.network === Services.INSTAGRAM_GRAPH
              ? selectedConnections.find((connection) => connection.type === Services.FACEBOOK)
              : selectedConnections.find((connection) => connection.type === item.network);
          if (!currentNetwork) {
            return false;
          }
          const networkSignInTime =
            item.network === Services.INSTAGRAM_GRAPH
              ? networkSignInTimes.find((network) => network.type === Services.FACEBOOK)?.connectedDate
              : networkSignInTimes.find((network) => network.type === item.network)?.connectedDate;
          return (
            item.error &&
            (!networkSignInTime ? true : new Date(item.pullTime).getTime() > new Date(networkSignInTime).getTime())
          );
        });
      return errors.length ? { [keyword.keyword]: errors } : null;
    })
    .filter((item) => item);
}

export function extractKeywordFeedErrors(keywords: Keyword[], socialNetworks: SocialNetworks = {} as SocialNetworks) {
  return keywords
    .map((keyword: Keyword) => {
      if (!keyword.feeds) {
        return null;
      }
      const errors = Object.entries(keyword.feeds)
        .map(([key]) => {
          const error = getOr('', 'pullInfo.error', keyword.feeds[key]);
          const pullTime = getOr('', 'pullInfo.pullTime', keyword.feeds[key]);
          const filteredError = error.includes('Not signed into') ? null : error;
          return { network: key, error: filteredError, pullTime };
        })
        .filter((item) => {
          const currentNetwork =
            item.network === Services.INSTAGRAM_GRAPH ? socialNetworks[Services.FACEBOOK] : socialNetworks[item.network];
          if (!currentNetwork) {
            return false;
          }
          const networkSignInTime =
            item.network === Services.INSTAGRAM_GRAPH
              ? socialNetworks[Services.FACEBOOK].connectedDate
              : socialNetworks[item.network].connectedDate;
          return (
            item.error &&
            (!networkSignInTime ? true : new Date(item.pullTime).getTime() > new Date(networkSignInTime).getTime())
          );
        });
      return errors.length ? { [keyword.keyword]: errors } : null;
    })
    .filter((item) => item);
}

export function popupWindow(url: string, title: string, win: Window, w: number, h: number) {
  const y = win.top ? win.top.outerHeight / 2 + win.top.screenY - h / 2 : 0;
  const x = win.top ? win.top.outerWidth / 2 + win.top.screenX - w / 2 : 0;
  return win.open(
    url,
    title,
    `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${y}, left=${x}`,
  );
}

export function getUserAdmin(user = {} as User) {
  return user && user.admin ? { role: Roles.GIGG_ADMIN } : {};
}

export function adminGetOrgIdByUrl() {
  const route = matchPath({ path: '/admin/orgs/:orgId' }, window.location.pathname);
  return get('params.orgId', route);
}

export function upperCaseFirst(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function shouldCensor(
  media: StoryMedia[],
  adultRestrictedLevel: string = 'UNLIKELY',
  racyRestrictedLevel: string = 'UNLIKELY',
) {
  for (let i = 0; i < media.length; i++) {
    if (
      media[i] &&
      media[i].safeSearchAnnotation &&
      (VisionLikelihood[media[i].safeSearchAnnotation.adult].SEVERITY > VisionLikelihood[adultRestrictedLevel].SEVERITY ||
        VisionLikelihood[media[i].safeSearchAnnotation.racy].SEVERITY > VisionLikelihood[racyRestrictedLevel].SEVERITY)
    ) {
      return true;
    }
  }
  return false;
}

export function getCurrentEntityType(pathname = window.location.pathname, alias?: boolean) {
  if (alias) {
    return pathname.split('/')[2] || '';
  }
  return getTypeFromAlias(pathname.split('/')[2] || '');
}

export function getAliasFromType(passedType: string) {
  if (!passedType) {
    return passedType;
  }
  if (passedType === EntityTypes.BOARD) {
    return EntityAliases.BOARD;
  }
  return EntityAliases[passedType.toUpperCase()];
}

export function getTypeFromAlias(passedType: string) {
  if (!passedType) {
    return '';
  }
  if (passedType === EntityAliases.BOARD) {
    return EntityTypes.BOARD;
  }
  return EntityTypes[passedType.toUpperCase()];
}

export function getCurrentEntityId(pathname = window.location.pathname) {
  const route = matchPath({ path: `/:orgPath/${getCurrentEntityType(pathname, true)}/:entityId` }, pathname);
  return get('params.entityId', route);
}

export function getCurrentFolderId(pathname = window.location.pathname) {
  const route = matchPath({ path: `/:orgPath/${getCurrentEntityType(pathname, true)}/:entityId/board/:boardId` }, pathname);
  return get('params.boardId', route) || '';
}

export function getCurrentEntityFromEntities(entities: Entities, pathname = window.location.pathname): Entity | undefined {
  const currentEntityType = getCurrentEntityType(pathname, true);
  const currentEntityId = getUrlParam(
    pathname,
    `/:orgPath/${currentEntityType}/:${currentEntityType}Id`,
    `${currentEntityType}Id`,
  );
  return entities.find((entity: Entity) => entity._id === currentEntityId);
}

export function roundTimeQuarterHour(time: number) {
  const timeToReturn = new Date(time);
  timeToReturn.setMilliseconds(Math.round(timeToReturn.getMilliseconds() / 1000) * 1000);
  timeToReturn.setSeconds(Math.round(timeToReturn.getSeconds() / 60) * 60);
  timeToReturn.setMinutes(Math.round(Math.ceil(timeToReturn.getMinutes() / 15)) * 15);
  return timeToReturn;
}

export function numPad(num: number, size: number) {
  const s = `000000000${num}`;
  return s.substr(s.length - size);
}

export function getStageFromRoute(props: any) {
  const stage = get('match.params.stage', props);

  if (stage) {
    return stage - 1;
  }

  return stage;
}

export function getStageFromParams(params: any) {
  const { stage } = params;

  if (stage) {
    return stage - 1;
  }

  return stage;
}

export function getCompetitionEmbedCode(competitionId: string) {
  return `<div id="gigg-competition"></div>
  <script src="${environment.fanboardUrl}"></script>
  <script type="text/javascript">
    window.addEventListener('message', function(event) {
      if (event.origin !== '${process.env.REACT_APP_BASE_URL}') {
        return;
      }
      if (event.data.type === 'redirect') {
        window.location.href = event.data.url;
      }
    }, false);
  </script>
  <script type="text/javascript">
    bragify.giggCompetition({
        domElementId: 'gigg-competition',
        compId: '${competitionId}',
        type: 'basic-competition',
        mode: 'iframe'
      });
  </script>`;
}

export function getBracketCompetitionEmbedCode(competitionId: string) {
  return `<div id="gigg-competition"></div>
  <script src="${environment.fanboardUrl}"></script>
  <script type="text/javascript">
    bragify.giggCompetition({
        domElementId: 'gigg-competition',
        compId: '${competitionId}',
        type: 'bracket'
      });
  </script>`;
}

export function getSubmissionFormEmbedCode(submissionFormUrl: string) {
  return `<iframe
    src="${submissionFormUrl}"
    width="100%"
    height="800"
    frameborder="0"
    style="border: none; overflow: hidden;"
    allowfullscreen
    allow="camera; microphone"
    sandbox="allow-scripts allow-same-origin allow-forms"
  ></iframe>`;
}

export function numberWithCommas(x: number) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function removeSpecialChars(string: string) {
  return string.replace(/[`#@~!$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/gi, '');
}

export function sanitizeUsername(string: string) {
  return string.replace(/[`#@~!$%^&*()_|+\-=?;:'",<>{}[\]\\\/]/gi, '');
}

export function correctURLProtocol(url: string) {
  if (!URL_PROTOCOL.test(url)) {
    return `https://${url}`;
  }

  return url;
}

export function correctSocialHandle(handle: string) {
  if (handle.includes('@')) return handle.replace('@', '');

  return handle;
}

export function handleFormikURLBlur(e: Event, formikOnBlur: Function, setFieldValue: Function, checkEmbedUrl?: Function) {
  const value = (e.target as HTMLInputElement).value;
  const name = (e.target as HTMLInputElement).name;

  if (checkEmbedUrl && name === 'embedUrl') {
    checkEmbedUrl(value);
  }

  setFieldValue(name, value);

  formikOnBlur(e);
  return value;
}

export function setPageTitleCount(count: number) {
  const regex = /\(\d{1,4}\)\s/;
  let title = document.title;
  if (regex.test(title)) {
    if (count < 1) {
      title = title.replace(regex, '');
    } else {
      title = title.replace(regex, `(${count}) `);
    }
  } else {
    if (count > 0) {
      title = `(${count}) ${title}`;
    }
  }

  document.title = title;
}

export function setPageTitle(page = 'Gigg') {
  document.title = `${page} | Gigg`;
}

// Makes handling unsaved form warnings easier by resetting the form to saved values thereby setting `dirty` to false
export function formikSubmitHandler(callback: Function) {
  return (values: FormikValues, formikActions: FormikActions<FormikValues>) => {
    callback(values, formikActions);
    formikActions.resetForm({ values });
  };
}

export async function createMediaFileFromUrl(url: string) {
  const separator = url.includes('?') ? '&' : '?';
  const res = await fetch(`${url}${separator}cacheBust=1`);
  const data = await res.blob();
  const metadata = {
    type: '',
  };
  if (data.type.startsWith('video')) {
    metadata.type = 'video/mp4';
    return new File([data], 'urlFile.mp4', metadata);
  } else {
    metadata.type = 'image/jpeg';
    return new File([data], 'urlFile.jpg', metadata);
  }
}

export function createImageFileFromB64(base64: string) {
  if (!base64) {
    return;
  }
  const data = base64toBlob(base64, 'image/jpeg');
  const metadata = {
    type: 'image/jpeg',
  };
  return new File([data], 'urlFile.jpg', metadata);
}

export function isImageFileType(fileType: string) {
  const imageTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  return imageTypes.includes(fileType);
}

export function getContentType(extension: string) {
  switch (extension) {
    case '.jpg':
    case '.jpeg':
      return 'image/jpeg';
    case '.png':
      return 'image/png';
    case '.gif':
      return 'image/gif';
    case '.webp':
      return 'image/webp';
    default:
      return null;
  }
}

function base64toBlob(base64Data: string, contentType: string) {
  contentType = contentType || '';
  const sliceSize = 1024;
  const byteCharacters = atob(base64Data);
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);

    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  return new Blob(byteArrays, { type: contentType });
}

export function validateURL(url: string) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function storyIsEditable(story: Story): boolean {
  return story.type === Services.CUSTOM || story.type === Services.OPEN_GRAPH;
}

export function urlToNetwork(url: string) {
  const twitterBaseRegex = /\btwitter.com\b/g;
  const youtubeBaseRegex = /\byoutube.com\b/g;
  const youtubeShortRegex = /\byoutu.be\b/g;
  const instagramBaseRegex = /\binstagram.com\b/g;
  const xBaseRegex = /\bx.com\b/g;
  const tikTokBaseRegex = /\btiktok.com\b/g;

  if (twitterBaseRegex.test(url) || xBaseRegex.test(url)) {
    return Services.TWITTER;
  }
  if (youtubeBaseRegex.test(url) || youtubeShortRegex.test(url)) {
    return Services.YOUTUBE;
  }
  if (instagramBaseRegex.test(url)) {
    return Services.INSTAGRAM_GRAPH;
  }
  if (tikTokBaseRegex.test(url)) {
    return Services.TIK_TOK;
  }
  return Services.OPEN_GRAPH;
}

export function lazyWithPreload(factory: any) {
  const Component = React.lazy(factory) as any;
  Component.preload = factory;
  return Component;
}

export function getPercentageChange(currentNum: number, prevNum: number) {
  if (!isFinite(prevNum) || prevNum === 0) return 0;
  return (currentNum - prevNum) / prevNum;
}

export function axisFormatWholeNumbers(d: number) {
  if (Math.floor(d) !== d) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(d);
}

export const getIsSiteCompetitionByType = (submissionType: string) => {
  return (
    submissionType === SubmissionTypes.UPLOADS ||
    submissionType === SubmissionTypes.SOCIAL_SITE ||
    submissionType === SubmissionTypes.BRACKET
  );
};

export const getCompetitionAdvancementType = (competition: Competition) => {
  const submissionType = competition?.metadata?.submissionType;
  const isSimpleCompetition = submissionType === SubmissionTypes.SOCIAL || submissionType === SubmissionTypes.COMMENTS;
  const isSimpleRandomCompetition =
    isSimpleCompetition && competition?.stages?.stage0?.advancement?.selectionType === AdvancementTypes.SUBMISSION;
  const isSimpleChoiceCompetition =
    isSimpleCompetition && competition?.stages?.stage0?.advancement?.selectionType === AdvancementTypes.SELECTED;

  return { isSimpleCompetition, isSimpleRandomCompetition, isSimpleChoiceCompetition };
};

export const getCurrentStageNum = (competition: Competition) => {
  const stageNum = competition?.currentStage?.stageNum;
  return stageNum;
};

export const isSocialLoggedIn = (socialNetworks: SocialNetworks) => {
  return true;
};

export const showSaveStatusMessage = (type: string, text?: string) => {
  if (type.includes('SUCCESS')) {
    message.success(text || 'Successfully saved.');
    return;
  }
  message.error(text || 'Failed to save.');
};

export const setCookie = (cName: string, cValue: string, exMinutes: number) => {
  const d = new Date();
  d.setTime(d.getTime() + exMinutes * 60 * 1000);
  const expires = `expires=${d.toUTCString()}`;
  return (document.cookie = `${cName}=${cValue};${expires};path=/;SameSite=None;Secure`);
};

export const getCookie = (cName: string) => {
  const name = `${cName}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i += 1) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
};

export const checkIsVideoUrl = (cdnUrl: string) => {
  const videoTypes = ['.quicktime', '.x-ms-wmv', '.wmv', '.x-msvideo', '.mpeg', '.x-flv', '.mp4', '.3gpp', '.webm'];
  return videoTypes.some((extension) => cdnUrl.includes(extension));
};

export const trimArr = (originalArr: any[], excludesArr: any[]) => {
  return originalArr?.filter((arrItem: any) => !excludesArr?.includes(arrItem));
};

export const arrayToObj = <T extends { [key in K]: string }, K extends keyof T>(
  arr: T[],
  keyName: K,
): Record<string, Omit<T, K>> => {
  const result: Record<string, Omit<T, K>> = {};

  arr?.forEach((item) => {
    const { [keyName]: key, ...rest } = item;
    result[key] = rest;
  });

  return result;
};

export const isFileImage = (file: File) => {
  return file.type.includes('image');
};

// Corrects sidways images from ios devices
export const fixSidewaysImage = (file: File, next: Function) => {
  if (!isFileImage(file)) {
    next(file);
    return;
  }

  loadImage(
    file,
    (imgCanvas: HTMLCanvasElement) => {
      imgCanvas.toBlob((blob: any) => {
        const newImageFile = new File([blob], file.name, { type: blob.type });
        next(newImageFile);
      }, file.type);
    },
    { orientation: true, canvas: true },
  );
};

export const convertImageToJpeg = (file: File, callback: (newFile: File) => File) => {
  loadImage(
    file,
    (imgCanvas: HTMLCanvasElement) => {
      imgCanvas.toBlob((blob: Blob) => {
        const newImageFile = new File([blob], file.name, { type: blob.type });
        callback(newImageFile);
      }, 'image/jpeg');
    },
    { canvas: true },
  );
};

export async function getImageFileFromUrl(imageUrl: string) {
  const res = await fetch(imageUrl);
  const blob = await res.blob();
  const file = new File([blob], 'image.jpg', { type: blob.type });
  return file;
}

export const threatStatusToReadable = (status: string) => {
  const threatMap = {
    UNKOWN: 'Unkown',
    UNLIKELY: 'Unlikely',
    POSSIBLE: 'Possible',
    LIKELY: 'Likely',
    VERY_LIKELY: 'Very Likely',
  };
  return threatMap[status];
};

export const getFiltersFromQuery = (query: string, board?: Entity) => {
  const facets = getFacets(query);
  const filters = {};
  facets.forEach((f) => {
    const facetArray = filters[f.name] || [];

    if (board && f.name === 'folder') {
      if (f.value !== 'pinned') {
        const folderId = Object.keys(board.folders).find((fId) => {
          return board.folders[fId].name === f.value;
        });
        if (folderId) {
          facetArray.push(folderId);
        }
      }
    } else {
      facetArray.push(f.value);
    }

    filters[f.name] = facetArray;
  });

  return filters;
};

interface Facet {
  name: string;
  value: string;
}

const facetPattern = /([a-zA-Z#@]+:.*?)(?=\s+[a-zA-Z#@]+:|$)/g;

export const getFacets = (str: string): Facet[] => {
  const facetStrings = str.match(facetPattern) || [];

  const facets: Facet[] = [];

  facetStrings.forEach((f: string) => {
    const [name, ...rest] = f.split(':');
    const rawValue = rest.join(':').trim();

    if (name === 'keyword') {
      const values = rawValue
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
      values.forEach((value) => {
        facets.push({ name, value });
      });
    } else {
      facets.push({ name: name.trim(), value: rawValue });
    }
  });

  return facets;
};

export const getQueryWithoutFacets = (str: string) => {
  const query = str.replace(facetPattern, '').trim();
  return query;
};

export const labelToPath = (str: string) => {
  return str.toLowerCase().replace(/ /g, '-');
};

interface Page {
  hits: Story[];
  total: number;
}
interface Pages {
  pages: Page[];
}

export const updateQueryDataStoryItem = async (
  queryClient: QueryClient,
  queryKey: unknown[],
  itemId: string,
  updateFunction: (prevStory: Story) => Story,
) => {
  await queryClient.cancelQueries({ queryKey });
  const previousItems = queryClient.getQueryData(queryKey);
  queryClient.setQueryData<Pages>(queryKey, (old) => {
    if (!old) return;
    return {
      ...old,
      pages: old.pages.map((page) => {
        const indexToUpdate = page.hits.findIndex((story) => story.id === itemId);
        if (indexToUpdate === -1) return page;
        const entryToUpdate = updateFunction(page.hits[indexToUpdate]);
        const newHits = [...page.hits];
        newHits.splice(indexToUpdate, 1, entryToUpdate);
        return {
          ...page,
          hits: newHits,
        };
      }),
    };
  });
  return { previousItems };
};

export const getToBlockArray = (stories: Story[], selectedStoryIds: string[]) => {
  const blockSet = new Set();
  selectedStoryIds.forEach((id) => {
    const story = stories?.find((s) => s.id === id);
    if (story?.username) {
      blockSet.add(`${story.username}:${story.type}`);
    }
  });

  return Array.from(blockSet).map((s: string) => {
    const [username, type] = s.split(':');
    return { username, type };
  });
};

// TODO: Something about our image upload process is making this necessary. It's not clear why.
// The url we get back is not correct, so we need to fix it.
export const getAssetImage = (path: string) => {
  // Remove any part of path before /images/
  const pathParts = path.split('/images/');
  if (pathParts.length > 1) {
    return `${environment.s3ImageUrl || ''}/images/${pathParts[1]}`;
  }
  return `${environment.s3ImageUrl || ''}${path}`;
};

export const findLastOrgConnection = (connections: OrgConnection[], type: string) => {
  return connections.reduce<OrgConnection | undefined>((prev, current) => {
    if (current.type !== type) return prev;
    return !prev ? current : new Date(prev.connectedDate) < new Date(current.connectedDate) ? current : prev;
  }, undefined);
};

export const getPublishedFoldersCount = (boards) => {
  let count = 0;
  boards.forEach((board) => {
    for (const folder in board.folders) {
      if (board?.folders[folder].published) {
        count += 1;
      }
    }
  });

  return count;
};

const rules = [
  // if it says it's a webview, let's go with that
  'WebView',
  // iOS webview will be the same as safari but missing "Safari"
  '(iPhone|iPod|iPad)(?!.*Safari)',
  // https://developer.chrome.com/docs/multidevice/user-agent/#webview_user_agent
  'Android.*Version/[0-9].[0-9]',
  // Also, we should save the wv detected for Lollipop
  // Android Lollipop and Above: webview will be the same as native but it will contain "wv"
  'Android.*wv',
  // old chrome android webview agent
  'Linux; U; Android',
];

const webviewRegExp = new RegExp('(' + rules.join('|') + ')', 'ig');

export function isWebview(ua: string) {
  return !!ua.match(webviewRegExp);
}
